/**
 * Pagina Gestione: le funzioni di amministrazione in card.
 *
 * Andrea 2026-09-29: divisa in due sezioni come il menu mobile, dalla
 * stessa fonte (`shell/adminLinks`):
 * - Admin · Lega — decisioni della lega corrente (calcolo giornate, stagioni,
 *   regolamento, utenti, asta, scommesse...)
 * - Admin · App — dati comuni a tutte le leghe (foglio voti, listone, TM) e
 *   manutenzione; solo per l'amministratore dell'app.
 */

import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { MobileShell } from "../shell/MobileShell";
import {
  type AdminLink,
  adminAppVisibili,
  adminLegaVisibili,
  ruoliUtente,
} from "../shell/adminLinks";
import { tr } from "../../i18n";

const RUOLO_LABEL: Record<string, string> = {
  presidente_lega: tr("PRESIDENTE LEGA"),
  admin: tr("ADMIN LEGA"),
  tesoriere: tr("TESORIERE"),
  mod_tm: tr("MOD TRANSFERMARKT"),
  mod_mercato: tr("MOD MERCATO"),
  partecipante: tr("PARTECIPANTE"),
};

const RUOLO_DESC: Record<string, string> = {
  presidente_lega: tr("Accesso completo · Veto attivo"),
  admin: tr("Calcolo giornate, mercato, utenti della lega"),
  tesoriere: tr("Tesoreria reale + quote / premi"),
  mod_tm: tr("Override valori Transfermarkt"),
  mod_mercato: tr("Conferma riscatti ODR condizionati"),
  partecipante: tr("Solo gestione propria squadra"),
};

function Sezione({
  titolo,
  descrizione,
  links,
}: {
  titolo: string;
  descrizione: string;
  links: AdminLink[];
}) {
  if (links.length === 0) return null;
  return (
    <section className="space-y-2">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-wider text-accent">{titolo}</div>
        <div className="text-[11px] text-fg-muted">{descrizione}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {links.map((tile) => (
          <Link
            key={tile.to}
            to={tile.to}
            className="relative bg-bg-card border border-primary/20 rounded-card p-3 hover:bg-bg-card-hover hover:border-accent/40 transition"
          >
            <div className="text-2xl mb-2">{tile.icon}</div>
            <div className="font-bold text-sm text-fg">{tile.label}</div>
            <div className="text-[10px] text-fg-muted mt-0.5">{tile.subtitle}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function GestionePage() {
  const { user } = useAuth();
  if (!user) return null;

  const ruoli = ruoliUtente(user);
  const lega = adminLegaVisibili(user);
  const app = adminAppVisibili(user);

  const ruoloDisplay = ruoli.has("presidente_lega")
    ? "presidente_lega"
    : ruoli.has("admin")
      ? "admin"
      : user.ruolo_lega;

  return (
    <MobileShell logoSuffix="GESTIONE">
      {/* Banner ruolo */}
      <div className="bg-gradient-to-br from-bg-card to-primary-deep border border-accent/30 rounded-card p-4 flex items-center gap-3">
        <div className="text-3xl">🔐</div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base text-accent tracking-wider">
            {RUOLO_LABEL[ruoloDisplay] ?? ruoloDisplay.toUpperCase()}
            {user.is_admin_app && <span className="text-fg-muted"> {tr("· ADMIN APP")}</span>}
          </div>
          <div className="text-xs text-fg-muted">{RUOLO_DESC[ruoloDisplay] ?? ""}</div>
        </div>
      </div>

      {lega.length === 0 && app.length === 0 && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center text-fg-muted">
          {tr("Nessuna funzione di gestione disponibile per il tuo ruolo.")}
        </div>
      )}

      <Sezione
        titolo="Admin · Lega"
        descrizione={tr(
          "Le decisioni di {{v1}}: calcolo e modifica delle giornate, stagioni, regole, utenti.",
          { v1: user.lega_corrente?.nome ?? "questa lega" },
        )}
        links={lega}
      />
      <Sezione
        titolo="Admin · App"
        descrizione="Dati comuni a tutte le leghe: il foglio voti caricato qui vale per ogni lega."
        links={app}
      />
    </MobileShell>
  );
}
